import { Cascader } from 'antd';
import type { CascaderOptionType, CascaderValueType } from 'antd/lib/cascader';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Box, Text } from '@frontend/components';

import { initMaterialsStore } from 'store/materials/actions';
import {
  getStoredMaterialOptionsForCascader,
  getMaterialClassByMaterialId,
  getMaterialFamilyByMaterialId,
} from 'store/materials/reducer';

const Highlight = ({ children }: { children: string }) => (
  <Text as="span" color="#0066ff">
    {children}
  </Text>
);

const searchFilter = (inputValue: string, path: CascaderOptionType[]) => {
  const showPath = path.some((option) => {
    const label = option.label as string;
    return label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
  });

  return path[path.length - 1].isLeaf ? showPath : false;
};

const highlight = (needle: string, haystack: string) => {
  let formattedStr = needle.replace(/\(/g, '\\(');
  formattedStr = formattedStr.replace(/\)/g, '\\)');
  let exp: RegExp | string;
  try {
    exp = new RegExp(`(${formattedStr})`, 'gi');
  } catch {
    exp = formattedStr;
  }
  const parts = haystack.split(exp);
  return (
    /* eslint-disable react/no-array-index-key */
    <span>
      {parts.map((part, index) => {
        if (typeof exp === 'string') {
          if (part === exp) {
            return <Highlight key={index}>{part}</Highlight>;
          } else {
            return <span key={index}>{part}</span>;
          }
        } else {
          if (exp.test(part)) {
            return <Highlight key={index}>{part}</Highlight>;
          } else {
            return <span key={index}>{part}</span>;
          }
        }
      })}
    </span>
  );
};

const searchRender = (inputValue: string, path: CascaderOptionType[]) => {
  return path.map((option, index) => {
    const label = option.label as string;
    const node =
      label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
        ? highlight(inputValue, label)
        : label;
    return index === 0 ? node : [' / ', node];
  });
};

const singleRender = (label: string[]) => (
  <span>{label && label[label.length - 1]}</span>
);

const multiRender = (label: string[]) => label.join(' / ');

const connector = connect(
  (
    state,
    {
      cascaderOptions,
      materialId,
    }: {
      cascaderOptions?: CascaderOptionType[];
      materialId: number | null | string;
    },
  ) => ({
    materialOptions:
      cascaderOptions || getStoredMaterialOptionsForCascader(state), // "cascaderOptions" optionally specified
    materialClass: getMaterialClassByMaterialId(state, materialId),
    materialFamily: getMaterialFamilyByMaterialId(state, materialId),
  }),
  {
    initMaterialStore: initMaterialsStore,
  },
);

type MaterialSelectorOwnProps = {
  disabled: boolean; // passed as direct prop
  onChange: ({
    classId,
    familyId,
    materialId,
    supplierMaterialId,
  }: {
    classId: number | string;
    familyId: number | string;
    materialId: number | string;
    supplierMaterialId: number | string;
  }) => void; // passed as direct prop
  placeholder: string; // passed as direct prop
  materialId: number | null | string; // passed as direct prop, used when shouldSingleSelect is true
  shouldSingleSelect?: boolean; // passed as direct prop
  supplierMaterialId: number | null | string; // passed as direct prop
  valueArray?: CascaderValueType; // passed as direct prop, used when shouldSingleSelect is false
};

type MaterialSelectorProps = MaterialSelectorOwnProps & {
  materialOptions: CascaderOptionType[];
  materialFamily?: { id: number };
  materialClass?: { id: number };
  initMaterialStore: any;
};

const MaterialSelector = ({
  disabled, // passed as direct prop
  initMaterialStore,
  onChange, // passed as direct prop
  placeholder, // passed as direct prop
  materialClass,
  materialFamily,
  materialId, // passed as direct prop, used when shouldSingleSelect is true
  materialOptions,
  shouldSingleSelect = true, // passed as direct prop
  supplierMaterialId, // passed as direct prop
  valueArray, // passed as direct prop, used when shouldSingleSelect is false
}: MaterialSelectorProps) => {
  const [hasInitiated, setHasInitiated] = useState(false);

  useEffect(() => {
    if (materialOptions.length == 0 && !hasInitiated) {
      initMaterialStore();
      setHasInitiated(true);
    }
  }, [hasInitiated, initMaterialStore, materialOptions.length]);

  let value: CascaderValueType | null;
  const materialClassId = (materialClass || {}).id || '';
  const materialFamilyId = (materialFamily || {}).id || '';
  if (shouldSingleSelect && supplierMaterialId && materialId) {
    value = [materialClassId, materialFamilyId, materialId, supplierMaterialId];
  } else if (shouldSingleSelect && materialId) {
    value = [materialClassId, materialFamilyId, materialId];
  } else if (shouldSingleSelect) {
    value = null;
  } else {
    value = valueArray || null;
  }

  const handleCascaderChange = (value: CascaderValueType) => {
    onChange({
      classId: value[0],
      familyId: value[1],
      materialId: value[2],
      supplierMaterialId: value[3],
    });
  };

  return (
    <Box position="relative" backgroundColor="white_s">
      <Box
        as={Cascader}
        changeOnSelect={true}
        disabled={disabled}
        displayRender={shouldSingleSelect ? singleRender : multiRender}
        onChange={handleCascaderChange}
        options={materialOptions}
        placeholder={placeholder}
        showSearch={{ filter: searchFilter, render: searchRender }}
        value={value}
        aria-label="Select Material"
        sx={{
          width: '100%',

          ':focus input.ant-cascader-input': {
            borderColor: '#828282',
            boxShadow: 'none',
          },

          'input.ant-cascader-input': {
            width: '100%',
            backgroundColor: '#ffffff',
            border: '1px solid #dedede',
            borderRadius: '2px',
            outline: 'none',
            padding: '0.7em 0.65em',
            height: 'auto',
            fontFamily: 'inherit',
            fontSize: ' 14px',
            lineHeight: 1.15,
            color: '#333333',
          },

          'input.ant-cascader-input &::placeholder': {
            color: ' #747474',
          },

          'input.ant-cascader-input &:hover': {
            borderColor: '#aaaaaa',
          },

          '.ant-cascader-picker-arrow': {
            border: '1px solid',
            borderColor: 'transparent transparent #979797 #979797',
            transform: 'rotate(-45deg)',
            width: '0.6em',
            height: '0.6em',
            fontSize: '14px',
            right: '12px',
            zIndex: 0,
          },

          '.ant-cascader-picker-arrow ::before': {
            content: 'normal',
          },

          '.ant-cascader-picker-arrow > svg': {
            display: 'none',
          },

          '.ant-cascader-picker-label': {
            fontSize: '14px',
            left: '2px',
          },

          '.ant-cascader-picker-clear': {
            width: '15px',
            height: '15px',
          },
        }}
      />
    </Box>
  );
};

export default connector(MaterialSelector);
